import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import {
  Book,
  Key,
  Shield,
  Clock,
  Code,
  Copy,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Wallet,
  TrendingUp,
  FileText,
  ArrowLeftRight,
} from "lucide-react";

interface EndpointProps {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  title: string;
  description: string;
  parameters?: {
    name: string;
    type: string;
    required: boolean;
    description: string;
  }[];
  responseExample: object;
  responseFields: {
    field: string;
    type: string;
    description: string;
  }[];
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <pre className="bg-neutral-900 text-neutral-100 p-4 rounded-lg text-sm overflow-x-auto">
        <code>{code}</code>
      </pre>
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 text-neutral-400 hover:text-white"
        onClick={handleCopy}
      >
        {copied ? (
          <CheckCircle className="w-4 h-4 text-green-500" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
      </Button>
    </div>
  );
}

function EndpointCard({ endpoint }: { endpoint: EndpointProps }) {
  const [expanded, setExpanded] = useState(false);

  const methodColors = {
    GET: "bg-green-500",
    POST: "bg-blue-500",
    PUT: "bg-yellow-500",
    DELETE: "bg-red-500",
  };

  return (
    <Card className="mb-4">
      <CardHeader
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge className={`${methodColors[endpoint.method]} text-white font-mono`}>
              {endpoint.method}
            </Badge>
            <code className="text-sm font-mono">{endpoint.path}</code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{endpoint.title}</span>
            {expanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-6 pt-0">
          <p className="text-muted-foreground">{endpoint.description}</p>

          {endpoint.parameters && endpoint.parameters.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Parâmetros</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-2 font-medium">Nome</th>
                      <th className="text-left p-2 font-medium">Tipo</th>
                      <th className="text-left p-2 font-medium">Obrigatório</th>
                      <th className="text-left p-2 font-medium">Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpoint.parameters.map((param) => (
                      <tr key={param.name} className="border-t">
                        <td className="p-2 font-mono text-xs">{param.name}</td>
                        <td className="p-2">
                          <Badge variant="outline">{param.type}</Badge>
                        </td>
                        <td className="p-2">
                          {param.required ? (
                            <Badge className="bg-red-100 text-red-700">Sim</Badge>
                          ) : (
                            <Badge variant="secondary">Não</Badge>
                          )}
                        </td>
                        <td className="p-2 text-muted-foreground">{param.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div>
            <h4 className="font-medium mb-2">Exemplo de Resposta</h4>
            <CodeBlock code={JSON.stringify(endpoint.responseExample, null, 2)} />
          </div>

          <div>
            <h4 className="font-medium mb-2">Campos da Resposta</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-2 font-medium">Campo</th>
                    <th className="text-left p-2 font-medium">Tipo</th>
                    <th className="text-left p-2 font-medium">Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoint.responseFields.map((field) => (
                    <tr key={field.field} className="border-t">
                      <td className="p-2 font-mono text-xs">{field.field}</td>
                      <td className="p-2">
                        <Badge variant="outline">{field.type}</Badge>
                      </td>
                      <td className="p-2 text-muted-foreground">{field.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

const endpoints: EndpointProps[] = [
  {
    method: "GET",
    path: "/api/external/v1/account/balance",
    title: "Obter Saldo",
    description:
      "Retorna o saldo atual em BRLD na carteira blockchain da empresa, incluindo o endereço da carteira e informações da rede.",
    responseExample: {
      balance: 15000.5,
      balanceFormatted: "R$ 15.000,50",
      currency: "BRLD",
      walletAddress: "0x86e2b5052a7f4d22019fa7660c65ed46bd715fbe",
      networkName: "XDC Network Mainnet",
      lastUpdatedAt: "2026-01-21T16:07:45.261Z",
    },
    responseFields: [
      { field: "balance", type: "number", description: "Saldo em BRLD (valor numérico)" },
      { field: "balanceFormatted", type: "string", description: "Saldo formatado em reais" },
      { field: "currency", type: "string", description: "Moeda (sempre 'BRLD')" },
      { field: "walletAddress", type: "string", description: "Endereço da carteira blockchain" },
      { field: "networkName", type: "string", description: "Nome da rede blockchain" },
      { field: "lastUpdatedAt", type: "string", description: "Data/hora da última atualização (ISO 8601)" },
    ],
  },
  {
    method: "GET",
    path: "/api/external/v1/account/yields",
    title: "Obter Rendimentos",
    description:
      "Retorna informações sobre o rendimento diário e total acumulado das aplicações financeiras da empresa.",
    responseExample: {
      dailyYield: 12.5,
      dailyYieldFormatted: "R$ 12,50",
      totalYield: 350.75,
      totalYieldFormatted: "R$ 350,75",
      yieldPercentage: 2.35,
      calculationDate: "2026-01-20",
    },
    responseFields: [
      { field: "dailyYield", type: "number", description: "Rendimento do último dia útil" },
      { field: "dailyYieldFormatted", type: "string", description: "Rendimento diário formatado" },
      { field: "totalYield", type: "number", description: "Rendimento total acumulado" },
      { field: "totalYieldFormatted", type: "string", description: "Rendimento total formatado" },
      { field: "yieldPercentage", type: "number", description: "Percentual de rendimento sobre o principal" },
      { field: "calculationDate", type: "string", description: "Data base do cálculo (YYYY-MM-DD)" },
    ],
  },
  {
    method: "GET",
    path: "/api/external/v1/account/summary",
    title: "Obter Resumo Completo",
    description:
      "Retorna um resumo consolidado com saldo, rendimentos, informações de aplicações e dados do produto em uma única chamada. Ideal para dashboards.",
    responseExample: {
      balance: {
        balance: 15000.5,
        balanceFormatted: "R$ 15.000,50",
        currency: "BRLD",
        walletAddress: "0x86e2b5052a7f4d22019fa7660c65ed46bd715fbe",
        networkName: "XDC Network Mainnet",
        lastUpdatedAt: "2026-01-21T16:07:45.261Z",
      },
      yields: {
        dailyYield: 12.5,
        dailyYieldFormatted: "R$ 12,50",
        totalYield: 350.75,
        totalYieldFormatted: "R$ 350,75",
        yieldPercentage: 2.35,
        calculationDate: "2026-01-20",
      },
      applications: {
        total: 5,
        active: 3,
        redeemed: 2,
      },
      product: {
        cdiRate: 100,
        annualRate: 14.15,
      },
      timestamp: "2026-01-21T10:30:00Z",
    },
    responseFields: [
      { field: "balance", type: "object", description: "Objeto com informações de saldo (ver endpoint /balance)" },
      { field: "yields", type: "object", description: "Objeto com informações de rendimentos (ver endpoint /yields)" },
      { field: "applications.total", type: "number", description: "Total de aplicações realizadas" },
      { field: "applications.active", type: "number", description: "Aplicações ativas (rendendo)" },
      { field: "applications.redeemed", type: "number", description: "Aplicações já resgatadas" },
      { field: "product.cdiRate", type: "number", description: "Percentual do CDI (ex: 100 = 100% do CDI)" },
      { field: "product.annualRate", type: "number", description: "Taxa anual equivalente" },
      { field: "timestamp", type: "string", description: "Data/hora da consulta (ISO 8601)" },
    ],
  },
  {
    method: "GET",
    path: "/api/external/v1/account/operations",
    title: "Listar Operações",
    description:
      "Retorna o histórico de operações (depósitos e saques) com suporte a paginação. As operações são ordenadas da mais recente para a mais antiga.",
    parameters: [
      {
        name: "page",
        type: "number",
        required: false,
        description: "Número da página (começa em 1). Padrão: 1",
      },
      {
        name: "pageSize",
        type: "number",
        required: false,
        description: "Itens por página (máx: 100). Padrão: 20",
      },
    ],
    responseExample: {
      operations: [
        {
          id: "uuid-da-operacao",
          type: "DEPOSIT",
          amount: 10000.0,
          amountFormatted: "R$ 10.000,00",
          status: "MINTED",
          description: "Depósito PIX",
          txHash: "0x6a2a53d4e0f488e8459e1d266004ef8e24350fbdbdcc7effd5d7c1f28e5eb12f",
          createdAt: "2026-01-15T14:30:00Z",
          completedAt: "2026-01-15T14:35:00Z",
        },
      ],
      pagination: {
        page: 1,
        pageSize: 20,
        total: 45,
        totalPages: 3,
      },
    },
    responseFields: [
      { field: "operations[].id", type: "string", description: "ID único da operação (UUID)" },
      { field: "operations[].type", type: "string", description: "Tipo: 'DEPOSIT' ou 'WITHDRAWAL'" },
      { field: "operations[].amount", type: "number", description: "Valor da operação" },
      { field: "operations[].amountFormatted", type: "string", description: "Valor formatado" },
      { field: "operations[].status", type: "string", description: "Status: PENDING, PROCESSING, MINTED, COMPLETED, FAILED" },
      { field: "operations[].description", type: "string", description: "Descrição da operação" },
      { field: "operations[].txHash", type: "string", description: "Hash da transação blockchain (quando aplicável)" },
      { field: "operations[].createdAt", type: "string", description: "Data de criação (ISO 8601)" },
      { field: "operations[].completedAt", type: "string", description: "Data de conclusão (ISO 8601)" },
      { field: "pagination.page", type: "number", description: "Página atual" },
      { field: "pagination.pageSize", type: "number", description: "Itens por página" },
      { field: "pagination.total", type: "number", description: "Total de operações" },
      { field: "pagination.totalPages", type: "number", description: "Total de páginas" },
    ],
  },
];

export function CompanyApiDocsPage() {
  const baseUrl = import.meta.env.VITE_API_URL || "https://backend-production-86baf.up.railway.app";

  const curlExample = `curl -X GET "${baseUrl}/api/external/v1/account/summary" \\
  -H "X-API-Key: brld_pk_sua_api_key_aqui"`;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Book className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Documentação da API"
        description="Guia completo para integração com a API externa do BRLD"
      />

      {/* Introdução */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-5 h-5 text-primary" />
            Introdução
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            A API externa do BRLD permite que sua empresa acesse dados da conta programaticamente.
            Com ela você pode consultar saldo, rendimentos, histórico de operações e muito mais.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            <div className="flex items-start gap-3 p-4 border rounded-lg">
              <Wallet className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Saldo em Tempo Real</p>
                <p className="text-sm text-muted-foreground">Consulte o saldo BRLD da sua carteira</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 border rounded-lg">
              <TrendingUp className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Rendimentos</p>
                <p className="text-sm text-muted-foreground">Acompanhe rendimentos diários e acumulados</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 border rounded-lg">
              <ArrowLeftRight className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">Histórico</p>
                <p className="text-sm text-muted-foreground">Acesse o histórico completo de operações</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Autenticação */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-primary" />
            Autenticação
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            Todas as requisições à API devem incluir uma API Key válida no header <code className="bg-muted px-1 rounded">X-API-Key</code>.
          </p>

          <div className="space-y-2">
            <h4 className="font-medium">Como obter uma API Key:</h4>
            <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
              <li>Acesse a página <a href="/api-keys" className="text-primary hover:underline">API Keys</a></li>
              <li>Clique em "Nova API Key"</li>
              <li>Preencha o nome e configurações opcionais</li>
              <li>Copie e salve a key gerada (ela só aparece uma vez!)</li>
            </ol>
          </div>

          <div>
            <h4 className="font-medium mb-2">Exemplo de requisição:</h4>
            <CodeBlock code={curlExample} />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Shield className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Segurança</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Nunca compartilhe sua API Key</li>
                  <li>Não inclua a key em código frontend público</li>
                  <li>Use variáveis de ambiente para armazenar a key</li>
                  <li>Revogue keys comprometidas imediatamente</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Regras da API Key */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Regras e Limites
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-primary" />
                <h4 className="font-medium">Rate Limiting</h4>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• <strong>60 requisições</strong> por minuto</li>
                <li>• <strong>1000 requisições</strong> por hora</li>
                <li>• Limites podem ser customizados por key</li>
              </ul>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-primary" />
                <h4 className="font-medium">IP Whitelist</h4>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Configure IPs permitidos (opcional)</li>
                <li>• Bloqueio automático de IPs não autorizados</li>
                <li>• Deixe vazio para permitir qualquer IP</li>
              </ul>
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-2">Headers de Rate Limit na Resposta:</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-2 font-medium">Header</th>
                    <th className="text-left p-2 font-medium">Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="p-2 font-mono text-xs">X-RateLimit-Limit-Minute</td>
                    <td className="p-2 text-muted-foreground">Limite de requisições por minuto</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono text-xs">X-RateLimit-Remaining-Minute</td>
                    <td className="p-2 text-muted-foreground">Requisições restantes no minuto atual</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono text-xs">X-RateLimit-Limit-Hour</td>
                    <td className="p-2 text-muted-foreground">Limite de requisições por hora</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono text-xs">X-RateLimit-Remaining-Hour</td>
                    <td className="p-2 text-muted-foreground">Requisições restantes na hora atual</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Códigos de Erro */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Códigos de Erro
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-2 font-medium">Código</th>
                  <th className="text-left p-2 font-medium">Descrição</th>
                  <th className="text-left p-2 font-medium">Solução</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="p-2">
                    <Badge variant="destructive">401</Badge>
                  </td>
                  <td className="p-2 text-muted-foreground">API key inválida ou ausente</td>
                  <td className="p-2 text-muted-foreground">Verifique se a key está correta no header X-API-Key</td>
                </tr>
                <tr className="border-t">
                  <td className="p-2">
                    <Badge variant="destructive">403</Badge>
                  </td>
                  <td className="p-2 text-muted-foreground">API key revogada, expirada ou IP não autorizado</td>
                  <td className="p-2 text-muted-foreground">Crie uma nova key ou verifique as configurações de IP</td>
                </tr>
                <tr className="border-t">
                  <td className="p-2">
                    <Badge className="bg-yellow-500">429</Badge>
                  </td>
                  <td className="p-2 text-muted-foreground">Rate limit excedido</td>
                  <td className="p-2 text-muted-foreground">Aguarde o reset do limite (verifique headers)</td>
                </tr>
                <tr className="border-t">
                  <td className="p-2">
                    <Badge variant="destructive">500</Badge>
                  </td>
                  <td className="p-2 text-muted-foreground">Erro interno do servidor</td>
                  <td className="p-2 text-muted-foreground">Tente novamente ou entre em contato com o suporte</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Base URL */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-5 h-5 text-primary" />
            Base URL
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CodeBlock code={`${baseUrl}/api/external/v1`} />
          <p className="text-sm text-muted-foreground mt-2">
            Todos os endpoints devem ser precedidos por esta URL base.
          </p>
        </CardContent>
      </Card>

      {/* Endpoints */}
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Endpoints
        </h2>
        {endpoints.map((endpoint) => (
          <EndpointCard key={endpoint.path} endpoint={endpoint} />
        ))}
      </div>

      {/* Exemplos de Integração */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-5 h-5 text-primary" />
            Exemplos de Integração
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-medium mb-2">JavaScript / Node.js</h4>
            <CodeBlock
              code={`const API_KEY = 'brld_pk_sua_api_key';
const BASE_URL = '${baseUrl}/api/external/v1';

async function getSummary() {
  const response = await fetch(\`\${BASE_URL}/account/summary\`, {
    headers: {
      'X-API-Key': API_KEY
    }
  });

  if (!response.ok) {
    throw new Error(\`Erro: \${response.status}\`);
  }

  return response.json();
}

// Uso
const data = await getSummary();
console.log('Saldo:', data.balance.balanceFormatted);
console.log('Rendimento Total:', data.yields.totalYieldFormatted);`}
            />
          </div>

          <div>
            <h4 className="font-medium mb-2">Python</h4>
            <CodeBlock
              code={`import requests

API_KEY = 'brld_pk_sua_api_key'
BASE_URL = '${baseUrl}/api/external/v1'

def get_summary():
    headers = {'X-API-Key': API_KEY}
    response = requests.get(f'{BASE_URL}/account/summary', headers=headers)
    response.raise_for_status()
    return response.json()

# Uso
data = get_summary()
print(f"Saldo: {data['balance']['balanceFormatted']}")
print(f"Rendimento Total: {data['yields']['totalYieldFormatted']}")`}
            />
          </div>

          <div>
            <h4 className="font-medium mb-2">cURL</h4>
            <CodeBlock
              code={`# Obter resumo completo
curl -X GET "${baseUrl}/api/external/v1/account/summary" \\
  -H "X-API-Key: brld_pk_sua_api_key"

# Obter operações com paginação
curl -X GET "${baseUrl}/api/external/v1/account/operations?page=1&pageSize=10" \\
  -H "X-API-Key: brld_pk_sua_api_key"`}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
